/**
 * CapturePanel.jsx — coverage-guided capture + auto-finalize for the Launch
 * workspace (WP2, docs/coverage-guided-capture-spec.md §6).
 *
 * The human's only job is to tap through the app. This panel:
 *   1. subscribes to the proxy's accepted live events (TrafficContext's
 *      `subscribeMessages`) while capture is running and feeds each row's
 *      ad-traffic category into the WP1 reducer (utils/captureMode.js);
 *   2. re-runs the launch's suite UNRECORDED (`record:false`) on a debounce
 *      whenever new rows have landed, so the per-criterion checklist below
 *      flips waiting → partial → covered live;
 *   3. when every traffic-verifiable criterion is covered AND ad traffic has
 *      gone quiet, runs the suite once more RECORDED, attaches the run to the
 *      launch, and writes a sign-off DRAFT onto the launch record.
 *
 * Division of labour (spec §5): this component owns the clock (ONE 2s
 * setInterval), the WebSocket subscription and every fetch; `captureMode.js`
 * owns every decision (`shouldLiveRun` / `shouldAutoFinalize` / quiet timer).
 * Nothing here re-derives a decision the reducer already makes.
 *
 * Invariants honoured (spec §2):
 *   - live checklist runs are UNRECORDED (`record:false`) — only the finalize
 *     run reaches run history / trend scoring;
 *   - the draft is a DRAFT: `launch.signoff` is never touched, and the report
 *     window is never auto-opened (popup blockers, no surprise windows);
 *   - no LLM calls, no api-key gate;
 *   - every failure surfaces inline — nothing is swallowed to the console.
 *
 * This is the LIVE half of the Launch page's single "Verify" panel — it renders
 * its own controls and progress but no card chrome and no heading, because
 * LaunchPage owns the panel it sits in. It also does not draw a per-criterion
 * checklist any more: it reports its results upward through `onLiveResults`
 * and the page's Criteria cards render them, labelled as coming from the live
 * capture. Before that, this panel kept its OWN result store and drew its own
 * copy of the criteria list, so one criterion could read "Waiting / Not run"
 * here and "Met" ten pixels below, with nothing on screen explaining why.
 *
 * Scope note: the live and final runs both post NO `rows`, so the proxy
 * scopes them to its own live `requestHistory` (apiServer.js's
 * /proxy/checks/run resolves rows from the posted `rows` or live history).
 * That is deliberate — a capture session is by definition "whatever the
 * device is producing right now", and it keeps the live re-run payload tiny.
 * It is also the same scope the finalize run uses, so the checklist the user
 * watches and the draft they get are computed over the same traffic.
 */
import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { useTraffic } from '../../context/TrafficContext';
import { runChecks } from '../../api/checks';
import { recordLaunchRun, saveLaunch } from '../../api/launches';
import { saveHarToServer } from '../../api/harClient';
import { persistRunEvidence } from '../../utils/evidenceHar';
import { launchCoverage } from '../../utils/launchCoverage';
import { launchRollup } from '../../utils/launchRollup';
import { openLaunchReport } from '../../utils/buildLaunchReport';
import {
  initialState,
  reduce,
  shouldLiveRun,
  shouldAutoFinalize,
  quietForMs,
  PHASE,
  QUIET_SEC,
} from '../../utils/captureMode';

// One clock for the whole panel (spec §6: "one 2s setInterval").
const TICK_MS = 2000;

/** Per-launch persistence of the auto-finalize toggle (spec §6.5). */
function autoFinalizeKey(launchId) {
  return `launchCapture.${launchId}.autoFinalize`;
}
function readStoredAutoFinalize(launchId) {
  try {
    const raw = localStorage.getItem(autoFinalizeKey(launchId));
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch { /* storage unavailable — fall back to the reducer default */ }
  return null;
}

/** results[] -> { checkId: result } (same shape launchRollup/launchCoverage read). */
function byCheckId(results) {
  const map = {};
  for (const r of Array.isArray(results) ? results : []) {
    if (r?.checkId) map[r.checkId] = r;
  }
  return map;
}

/**
 * The ad-traffic category of one accepted live event's row. The proxy stamps
 * `row.adCategory` top-level at capture time
 * (proxyOnRequestHandler.js — `requestDetails.adCategory = adt.adCategory`),
 * so the broadcast row carries it directly; the validData fallback covers a
 * proxy older than that stamp. `null` is meaningful, not a miss: the reducer
 * treats an uncategorised row as ad traffic (conservative — unknown traffic
 * holds quiescence open rather than letting a draft fire early).
 */
function categoryOfEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  if (typeof entry.adCategory === 'string') return entry.adCategory;
  const fromValidData = entry.validData?.adCategory;
  return typeof fromValidData === 'string' ? fromValidData : null;
}

export default function CapturePanel({ launch, onLaunchUpdated, tester = '', onLiveResults, evidenceRowsById }) {
  const { subscribeMessages } = useTraffic();

  const [state, dispatch] = useReducer(reduce, undefined, initialState);
  const [now, setNow] = useState(() => Date.now());

  // Latest UNRECORDED checklist run's results (the live checklist's source of
  // truth) and the last error from either loop — both surfaced inline.
  const [liveResults, setLiveResults] = useState([]);
  // When the live results were last refreshed — the page labels the Criteria
  // cards with it so "Live capture 7:41 PM" vs "Batch run 6:02 PM" is legible
  // when the two disagree.
  const [liveResultsAt, setLiveResultsAt] = useState(null);
  const [liveRunning, setLiveRunning] = useState(false);
  const [liveError, setLiveError] = useState('');
  const [finalizeError, setFinalizeError] = useState('');
  // The sign-off draft produced by a successful finalize: enough to open the
  // report on demand. Never auto-opened.
  const [draft, setDraft] = useState(null); // { runId, at, resultsByCheckId }
  const [toastOpen, setToastOpen] = useState(false);

  // In-flight guards — never more than one outstanding call of either kind.
  const liveRunInFlight = useRef(false);
  const finalizeInFlight = useRef(false);
  // Live-event unsubscribe fn, called on stop AND on unmount.
  const unsubRef = useRef(null);
  // Freshest tick handler, so the interval below is created exactly once per
  // capture session instead of being torn down on every state change.
  const tickRef = useRef(null);

  const criteria = useMemo(() => (Array.isArray(launch?.criteria) ? launch.criteria : []), [launch]);
  const resultsByCheckId = useMemo(() => byCheckId(liveResults), [liveResults]);
  const coverage = useMemo(() => launchCoverage(criteria, resultsByCheckId), [criteria, resultsByCheckId]);

  // Hand the live results to the page, which renders them in the Criteria
  // cards labelled "Live capture" alongside the batch run's. This panel used to
  // keep them to itself and draw its own checklist — the reason a criterion
  // could show two different verdicts on one screen.
  useEffect(() => {
    if (typeof onLiveResults === 'function') onLiveResults(liveResults, liveResultsAt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveResults, liveResultsAt, onLiveResults]);

  const active = state.phase !== PHASE.IDLE;
  // Traffic-verifiable criteria: everything that isn't manual/unmapped.
  const trackable = coverage.covered + coverage.partial + coverage.waiting;
  const quietMs = quietForMs(state, now);

  // ── Seed the reducer's auto-finalize toggle from localStorage (§6.5).
  // Default is ON, so only a stored `false` needs a dispatch. Runs once per
  // mount; LaunchPage keys this panel by launch id, so a launch switch
  // remounts with that launch's own stored preference.
  useEffect(() => {
    const stored = readStoredAutoFinalize(launch?.id);
    if (stored === false) dispatch({ type: 'toggleAutoFinalize' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Live traffic subscription — only while a capture session is running.
  // The cleanup covers both "stop" (active flips false) and unmount.
  useEffect(() => {
    if (!active) return undefined;
    const unsub = subscribeMessages((data, newEntry) => {
      // `emit(data, newEntry)` (TrafficContext.js:512) delivers ACCEPTED live
      // events only — one row per event, already deduped/unpaused — plus a
      // no-entry history_cleared notification we ignore here.
      if (!newEntry) return;
      dispatch({ type: 'rowsArrived', ts: Date.now(), categories: [categoryOfEntry(newEntry)] });
    });
    unsubRef.current = unsub;
    return () => {
      unsubRef.current = null;
      unsub();
    };
  }, [active, subscribeMessages]);

  // ── The two async jobs the tick can start. Redefined each render so they
  // close over fresh props/state; only ever entered through the in-flight
  // guards above.
  async function doLiveRun() {
    liveRunInFlight.current = true;
    setLiveRunning(true);
    try {
      const res = await runChecks({ suite: launch.suite, app: launch.app || undefined, record: false });
      setLiveResults(Array.isArray(res?.results) ? res.results : []);
      setLiveResultsAt(Date.now());
      setLiveError('');
    } catch (e) {
      setLiveError(e?.message || 'Live checklist run failed.');
    } finally {
      liveRunInFlight.current = false;
      setLiveRunning(false);
      // Dispatched on failure too: the debounce floor doubles as a back-off
      // so a broken/unreachable proxy is retried every MIN_RUN_INTERVAL_SEC
      // rather than on every tick.
      dispatch({ type: 'liveRunCompleted', ts: Date.now() });
    }
  }

  async function doFinalize() {
    finalizeInFlight.current = true;
    setFinalizeError('');
    dispatch({ type: 'finalizeStarted' });
    try {
      // (a) recorded run of the launch's own suite. `mode: 'live'` (WP6,
      // docs/mcp-signoff-spec.md §5) tags this run so the proxy's launch
      // rollup can tell it apart from LaunchPage's batch run — both call
      // runChecks() with record:true, so `trigger` alone can't distinguish them.
      const res = await runChecks({ suite: launch.suite, app: launch.app || undefined, record: true, mode: 'live', gradedIds: true });
      const results = Array.isArray(res?.results) ? res.results : [];
      const runId = res?.runId ?? null;
      if (!runId) throw new Error('The proxy recorded no run id for this run — draft not written.');
      const finalResults = byCheckId(results);

      // (b) save the evidence HAR and record + attach the run
      // (docs/ticket-validation-workflow-spec.md §4). A failed HAR save is
      // reported on the snapshot, not thrown; only a failed attach aborts.
      const evidence = await persistRunEvidence({
        launch, results, runId, mode: 'live',
        rowsById: evidenceRowsById || {}, saveHar: saveHarToServer, recordRun: recordLaunchRun,
      });
      if (evidence.error) throw new Error(`Run not attached to the launch (${evidence.error}) — draft not written.`);
      const attached = { launch: evidence.launch };

      // (c) write the DRAFT onto the launch record. Launch records tolerate
      // unknown keys, so this needs no schema migration — and it deliberately
      // does NOT touch launch.signoff (spec §2: sign-off stays
      // acknowledgement-gated and human).
      // Base the save on the launch the run record just returned, NOT this
      // component's `launch` prop: sanitizeLaunch (launches.js) lets a posted
      // runIds array WIN over the stored one, so saving the stale client copy
      // here would silently drop the runId attached in (b).
      const base = attached?.launch || launch;
      const at = new Date().toISOString();
      const autoDraft = {
        runId,
        at,
        rollup: launchRollup(criteria, finalResults),
        coverage: launchCoverage(criteria, finalResults),
      };
      const saved = await saveLaunch({ ...base, autoDraft });
      if (saved?.ok && saved?.launch && typeof onLaunchUpdated === 'function') onLaunchUpdated(saved.launch);

      // (d) surface the draft — report opened on demand only.
      setLiveResults(results);
      setLiveResultsAt(at || Date.now());
      setDraft({ runId, at, resultsByCheckId: finalResults });
      setToastOpen(true);
      dispatch({ type: 'finalizeSucceeded' });
    } catch (e) {
      setFinalizeError(e?.message || 'Could not produce the sign-off draft.');
      dispatch({ type: 'finalizeFailed' });
    } finally {
      finalizeInFlight.current = false;
    }
  }

  // ── The tick: advance the displayed clock, then ask the reducer what (if
  // anything) to do. Every decision belongs to captureMode.js.
  function onTick() {
    const ts = Date.now();
    setNow(ts);
    if (!liveRunInFlight.current && shouldLiveRun(state, ts)) {
      doLiveRun();
      return;
    }
    if (!finalizeInFlight.current && shouldAutoFinalize(state, coverage, ts)) {
      doFinalize();
    }
  }
  // Keep the interval pointed at the freshest closure without restarting it.
  useEffect(() => { tickRef.current = onTick; });

  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => { if (tickRef.current) tickRef.current(); }, TICK_MS);
    return () => clearInterval(id);
  }, [active]);

  function handleStart() {
    setLiveError('');
    setFinalizeError('');
    dispatch({ type: 'start', ts: Date.now() });
  }

  function handleStop() {
    // Explicit unsubscribe as well as the effect cleanup — the Set-backed
    // listener registry makes a double-remove a no-op.
    if (unsubRef.current) {
      try { unsubRef.current(); } catch { /* already removed */ }
      unsubRef.current = null;
    }
    dispatch({ type: 'stop' });
  }

  function handleToggleAutoFinalize(e) {
    const next = !!e.target.checked;
    try { localStorage.setItem(autoFinalizeKey(launch?.id), next ? 'true' : 'false'); } catch { /* non-fatal */ }
    dispatch({ type: 'toggleAutoFinalize' });
  }

  function handleOpenDraft() {
    if (!draft) return;
    openLaunchReport(launch, draft.resultsByCheckId, {
      tester,
      runId: draft.runId || '',
      app: launch?.app || '',
    });
  }

  const finalizing = state.phase === PHASE.FINALIZING;
  // "Finalize now" ignores quiescence by design (spec §6.5) and works even
  // before capture is started — it is just "run recorded + write the draft".
  // Doing it from idle does move the panel into the reducer's
  // finalizing → drafted path, so any traffic afterwards re-arms capture and
  // marks that draft stale; that is the same re-arm contract a normal
  // session gets, so it stays coherent rather than being special-cased.
  const canFinalize = trackable > 0 && !finalizing;
  const finalizeDisabledReason = trackable === 0
    ? 'Every criterion here is manual or unmapped — there is nothing traffic can verify, so there is nothing to finalize.'
    : '';

  // No <Paper>, no heading: LaunchPage owns the "Verify" card this renders
  // inside, and a second card with its own title was half of why the page read
  // as two competing tools.
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {active ? (
          <Button size="small" variant="outlined" onClick={handleStop}>Stop capture</Button>
        ) : (
          <Button size="small" variant="contained" onClick={handleStart}>Start capture</Button>
        )}
        {active && <Chip size="small" label={state.phase} color={finalizing ? 'info' : 'success'} sx={{ fontWeight: 700 }} />}
        <FormControlLabel
          control={<Switch size="small" checked={!!state.autoFinalizeEnabled} onChange={handleToggleAutoFinalize} />}
          label={<Typography variant="caption">auto-finalize</Typography>}
        />
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Tap through the app on the connected device — the criteria below fill in as traffic
        proves each one. When everything traffic can verify is covered and ad traffic has
        been quiet for {QUIET_SEC}s, a sign-off draft is produced automatically.
      </Typography>

      {/* ── Progress header ── */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5, mb: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          covered {coverage.covered} / {trackable}
          {' · '}manual {coverage.manual}
          {' · '}quiet for {quietMs == null ? '—' : `${Math.max(0, Math.round(quietMs / 1000))}s`}
        </Typography>
        {coverage.unmapped > 0 && (
          <Typography variant="caption" color="text.secondary">({coverage.unmapped} unmapped)</Typography>
        )}
        {coverage.complete && (
          <Chip size="small" label="fully covered" color="success" variant="outlined" sx={{ fontWeight: 700 }} />
        )}
        {liveRunning && <CircularProgress size={12} />}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Tooltip title={finalizeDisabledReason} disableHoverListener={!finalizeDisabledReason}>
          <span>
            <Button
              size="small"
              variant="outlined"
              disabled={!canFinalize}
              onClick={() => doFinalize()}
              startIcon={finalizing ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              {finalizing ? 'Finalizing…' : 'Finalize now'}
            </Button>
          </span>
        </Tooltip>
        {draft && (
          <>
            <Chip
              size="small"
              color={state.staleDraft ? 'warning' : 'success'}
              variant="outlined"
              label={state.staleDraft ? 'Draft — stale, session continued' : `Draft ready ${new Date(draft.at).toLocaleTimeString()}`}
              sx={{ fontWeight: 600 }}
            />
            <Button size="small" variant="contained" onClick={handleOpenDraft}>
              {state.staleDraft ? 'Open draft report (stale)' : 'Open draft report'}
            </Button>
          </>
        )}
      </Stack>

      {finalizeError && <Alert severity="error" sx={{ mb: 1 }}>{finalizeError}</Alert>}
      {liveError && <Alert severity="warning" sx={{ mb: 1 }}>{liveError}</Alert>}
      {state.staleDraft && draft && (
        <Alert severity="info" icon={false} sx={{ mb: 1, py: 0.5 }}>
          More traffic arrived after this draft was produced — it is stale, but still readable.
          It refreshes on the next quiet period.
        </Alert>
      )}

      {/* The per-criterion checklist that used to live here is gone: the page's
          Criteria cards render these same results, labelled "Live capture", so
          there is exactly one list and one place a verdict can come from. */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={12000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setToastOpen(false)}
          action={<Button color="inherit" size="small" onClick={handleOpenDraft}>Open draft report</Button>}
        >
          Sign-off draft ready
        </Alert>
      </Snackbar>
    </Box>
  );
}
